import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useCurrency, useI18n } from '~Hooks';
import { maxItemsQuantity } from '../../../../../General';
const move = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled.div<{ hidden?: boolean }>`
  position: fixed;
  z-index: 1;
  bottom: 115px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  animation: ${move} 0.7s ease-in;
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  transition: opacity 0.1s;

  @media (orientation: landscape) {
    bottom: 48px;
  }
`;

const Button = styled.div`
  height: 88px;
  width: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgb(255, 255, 255);
  border-radius: 32px;
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14),
    0px 1px 18px 0px rgba(0, 0, 0, 0.12);
  pointer-events: all;

  @media (orientation: landscape) {
    height: 66px;
    width: 90px;
    border-radius: 24px;
  }
`;

const MinusIcon = styled.span`
  background: rgb(33, 33, 36);
  height: 1.88px;
  width: 21.88px;
`;

const PlusIcon = styled.span`
  position: relative;
  width: 21.88px;
  height: 21.88px;

  &:before,
  &:after {
    content: '';
    position: absolute;
    background-color: rgb(33, 33, 36);
  }

  /* Vertical line */
  &:before {
    top: 0;
    left: 50%;
    width: 1.88px;
    height: 100%;
    margin-left: -0.94px;
  }

  /* horizontal line */
  &:after {
    top: 50%;
    left: 0;
    width: 100%;
    height: 1.88px;
    margin-top: -0.94px;
  }
`;

const PriceWrapper = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 192px;
  width: 192px;
  margin: 0 24px;
  background: ${({ active, theme }) =>
    active ? (theme.brandColor2 ? theme.brandColor2 : 'var(--colorWhite)') : 'rgb(158, 158, 161)'};
  border-radius: 72px;
  box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.32), 0px 9px 46px 8px rgba(0, 0, 0, 0.2);
  transition: 0.3s background;
  pointer-events: all;

  @media (orientation: landscape) {
    height: 144px;
    width: 144px;
    margin: 0 18px;
    border-radius: 54px;
  }
`;

const Price = styled.div`
  color: ${({ theme }) => theme.complimentaryColor2};
  font-family: Eina01-Regular;
  font-size: 32px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 40px;
  text-align: right;

  @media (orientation: landscape) {
    font-size: 20px;
    line-height: 24px;
  }
`;

const PriceLabel = styled.div`
  color: ${({ theme }) => theme.complimentaryColor2};
  font-family: Eina01-SemiBold;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  text-align: center;
`;

type Props = {
  isEdit?: boolean;
  cartItemsQuantity: number;
  hidden?: boolean;
  onChangeQuantity?: (quantity: number) => void;
  quantity: number;
  amount: number;
  onSubmit: () => void;
  valid: boolean;
};

const BottomButtons = ({
  isEdit,
  onChangeQuantity,
  quantity,
  amount,
  onSubmit,
  valid,
  hidden,
  cartItemsQuantity,
}: Props) => {
  const { i18n } = useI18n();
  const currency = useCurrency();

  const buttonEnabled =
    Boolean(valid && amount > 0 && cartItemsQuantity < maxItemsQuantity && !isEdit) ||
    Boolean(valid && amount > 0 && isEdit);

  return (
    <Wrapper hidden={hidden}>
      <Button onClick={onChangeQuantity ? () => onChangeQuantity(-1) : undefined}>
        <MinusIcon />
      </Button>
      <PriceWrapper onClick={buttonEnabled ? onSubmit : undefined} active={buttonEnabled}>
        <Price>{currency.format(amount)}</Price>
        <PriceLabel>{`${i18n('App.Add')} ${quantity} ${i18n('SSK.Piece')}`}</PriceLabel>
      </PriceWrapper>
      <Button onClick={onChangeQuantity ? () => onChangeQuantity(1) : undefined}>
        <PlusIcon />
      </Button>
    </Wrapper>
  );
};

export default BottomButtons;
